import { FC, useEffect, useRef } from 'react'
import ApexCharts, { ApexOptions } from 'apexcharts'

type Categorized = {
    pricing: number
    draft: number
    upcoming: number
    ongoing: number
    finished: number
}

type Props = {
    categorized: Categorized
}

// Fixed category order + colors so the donut matches the Project Status cards exactly.
const CATEGORIES: Array<{ key: keyof Categorized; label: string; color: string }> = [
    { key: 'pricing', label: 'Pricing', color: '#9333ea' },
    { key: 'draft', label: 'Draft', color: '#64748b' },
    { key: 'upcoming', label: 'Upcoming', color: '#0284c7' },
    { key: 'ongoing', label: 'Ongoing', color: '#d97706' },
    { key: 'finished', label: 'Finished', color: '#059669' },
]

export const ProjectStatusPieChart: FC<Props> = ({ categorized }) => {
    const chartRef = useRef<HTMLDivElement | null>(null)

    const total = CATEGORIES.reduce((sum, c) => sum + (categorized?.[c.key] || 0), 0)

    useEffect(() => {
        if (!chartRef.current || total === 0) {
            return
        }
        const labels = CATEGORIES.map((c) => c.label)
        const series = CATEGORIES.map((c) => categorized?.[c.key] || 0)
        const colors = CATEGORIES.map((c) => c.color)
        const chart = new ApexCharts(chartRef.current, getChartOptions(labels, series, colors))
        chart.render()
        return () => {
            chart.destroy()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [categorized, total])

    if (total === 0) {
        return (
            <div style={{ height: '280px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>
                No status data available
            </div>
        )
    }

    return <div ref={chartRef} style={{ height: '280px' }}></div>
}

function getChartOptions(labels: string[], series: number[], colors: string[]): ApexOptions {
    return {
        series,
        chart: {
            fontFamily: "'Hanken Grotesk', sans-serif",
            type: 'donut',
            height: 280,
            animations: { enabled: true, speed: 600 },
        },
        labels,
        plotOptions: {
            pie: {
                donut: {
                    size: '62%',
                    labels: {
                        show: true,
                        name: {
                            show: true,
                            fontSize: '13px',
                            fontFamily: "'Archivo', sans-serif",
                            fontWeight: 600,
                            color: '#334155',
                        },
                        value: {
                            show: true,
                            fontSize: '26px',
                            fontFamily: "'Archivo', sans-serif",
                            fontWeight: 800,
                            color: '#0a0f1c',
                        },
                        total: {
                            show: true,
                            label: 'Total',
                            fontSize: '12px',
                            fontFamily: "'JetBrains Mono', monospace",
                            fontWeight: 600,
                            color: '#64748b',
                        },
                    },
                },
            },
        },
        colors,
        legend: {
            show: true,
            position: 'bottom',
            fontFamily: "'Hanken Grotesk', sans-serif",
            fontSize: '13px',
            fontWeight: 500,
            labels: { colors: '#475569' },
            markers: { width: 9, height: 9, radius: 3 },
            itemMargin: { horizontal: 10, vertical: 4 },
        },
        dataLabels: { enabled: false },
        stroke: { show: true, width: 2, colors: ['#ffffff'] },
        tooltip: {
            style: { fontSize: '13px', fontFamily: "'Hanken Grotesk', sans-serif" },
            y: { formatter: (val) => `${val} projects` },
        },
        responsive: [
            {
                breakpoint: 480,
                options: { chart: { height: 260 }, legend: { position: 'bottom' } },
            },
        ],
    }
}
